import React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

/**
 * WhatsAppCategoryMiniTable - Mini-tabela "Tipo | Plataforma | Próprio | Total" reaproveitada
 * pelo bloco global de categorias (WhatsAppCategoryBreakdown) e pelo painel expandido de cada
 * empresa em WhatsAppCompaniesTable.
 * @param {Object} props
 * @param {Array<{key: string, label: string}>} props.categories - ordem/labels vinda do overview/companies
 * @param {Object} props.byCategory - { [key]: { platform, own, total } }, apenas chaves com total > 0
 * @param {string} props.emptyMessage
 * @param {boolean} props.showShare - exibe uma coluna extra com barra de participação no total
 */
function WhatsAppCategoryMiniTable({ categories = [], byCategory = {}, emptyMessage = 'Nenhum envio no período.', showShare = false }) {
  const rows = categories.filter((cat) => (byCategory[cat.key]?.total || 0) > 0);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  const grandTotal = rows.reduce((sum, cat) => sum + (byCategory[cat.key]?.total || 0), 0);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', border: 0, pl: 0 }}>Tipo</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', border: 0 }}>Plataforma</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', border: 0 }}>Próprio</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', border: 0, pr: showShare ? 2 : 0 }}>Total</TableCell>
          {showShare && (
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', border: 0, pr: 0, minWidth: 120 }}>
              Participação
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((cat) => {
          const entry = byCategory[cat.key] || {};
          const total = entry.total ?? 0;
          const share = grandTotal > 0 ? Math.round((total / grandTotal) * 100) : 0;
          return (
            <TableRow key={cat.key}>
              <TableCell sx={{ border: 0, pl: 0 }}>
                <Typography variant="body2">{cat.label || cat.key}</Typography>
              </TableCell>
              <TableCell align="center" sx={{ border: 0 }}>
                <Typography variant="body2">{entry.platform ?? 0}</Typography>
              </TableCell>
              <TableCell align="center" sx={{ border: 0 }}>
                <Typography variant="body2">{entry.own ?? 0}</Typography>
              </TableCell>
              <TableCell align="center" sx={{ border: 0, pr: showShare ? 2 : 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{total}</Typography>
              </TableCell>
              {showShare && (
                <TableCell sx={{ border: 0, pr: 0, minWidth: 120 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={share}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#EEF1F6',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#5A80FB' },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
                      {share}%
                    </Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default WhatsAppCategoryMiniTable;
